import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  PLATFORM_ID,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type * as Leaflet from 'leaflet';
import { LOCATIONIQ_MAP_TILE_URL } from '@/environments/environment';

export interface SelectedLocation {
  latitude: number;
  longitude: number;
  isPinConfirmed: boolean;
}

export interface MapViewport {
  latitude: number;
  longitude: number;
  zoom: number;
}

@Component({
  selector: 'app-location-picker',
  imports: [TranslateModule],
  templateUrl: './location-picker.html',
  styleUrl: './location-picker.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationPicker implements AfterViewInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly translate = inject(TranslateService);
  private readonly mapHost =
    viewChild.required<ElementRef<HTMLDivElement>>('mapHost');
  private leaflet?: typeof Leaflet;
  private map?: Leaflet.Map;
  private marker?: Leaflet.Marker;
  private destroyed = false;

  readonly latitude = input<number | null>(null);
  readonly longitude = input<number | null>(null);
  readonly isPinConfirmed = input(false);
  readonly viewport = input<MapViewport | null>(null);
  readonly locationChange = output<SelectedLocation>();

  readonly selectedLatitude = signal<number | null>(null);
  readonly selectedLongitude = signal<number | null>(null);
  readonly confirmed = signal(false);
  readonly geolocationError = signal<string | null>(null);
  readonly isLocating = signal(false);

  constructor() {
    effect(() => {
      const latitude = this.latitude();
      const longitude = this.longitude();
      const confirmed = this.isPinConfirmed();

      if (
        latitude === this.selectedLatitude() &&
        longitude === this.selectedLongitude() &&
        confirmed === this.confirmed()
      ) {
        return;
      }

      this.selectedLatitude.set(latitude);
      this.selectedLongitude.set(longitude);
      this.confirmed.set(confirmed && latitude !== null && longitude !== null);
      queueMicrotask(() => this.renderSelection(true));
    });

    effect(() => {
      const viewport = this.viewport();
      if (viewport) queueMicrotask(() => this.renderViewport(viewport));
    });
  }

  async ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    const leafletModule = (await import('leaflet')) as typeof Leaflet & {
      default?: typeof Leaflet;
    };
    this.leaflet = leafletModule.default ?? leafletModule;
    if (this.destroyed) return;
    this.map = this.leaflet
      .map(this.mapHost().nativeElement)
      .setView([33.5138, 36.2765], 12);
    this.leaflet
      .tileLayer(LOCATIONIQ_MAP_TILE_URL, {
        attribution:
          '&copy; <a href="https://locationiq.com/">LocationIQ Maps</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
        referrerPolicy: 'strict-origin-when-cross-origin',
      })
      .addTo(this.map);
    this.map.on('click', (event: Leaflet.LeafletMouseEvent) => {
      this.selectLocation(event.latlng.lat, event.latlng.lng);
    });
    const viewport = this.viewport();
    if (viewport) this.renderViewport(viewport);
    this.renderSelection(true);
    queueMicrotask(() => this.map?.invalidateSize({ pan: false }));
  }

  confirmSelection() {
    const latitude = this.selectedLatitude();
    const longitude = this.selectedLongitude();
    if (latitude === null || longitude === null) return;

    this.confirmed.set(true);
    this.emitSelection(latitude, longitude, true);
  }

  selectMapCenter() {
    const center = this.map?.getCenter();
    if (center) this.selectLocation(center.lat, center.lng);
  }

  useCurrentLocation() {
    this.geolocationError.set(null);
    if (!isPlatformBrowser(this.platformId) || !navigator.geolocation) {
      this.geolocationError.set('locationPicker.geolocationUnsupported');
      return;
    }

    this.isLocating.set(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        if (this.destroyed) return;
        this.isLocating.set(false);
        this.selectLocation(coords.latitude, coords.longitude, true);
      },
      (error) => {
        if (this.destroyed) return;
        this.isLocating.set(false);
        const errorKeys: Record<number, string> = {
          1: 'locationPicker.geolocationDenied',
          2: 'locationPicker.geolocationUnavailable',
          3: 'locationPicker.geolocationTimeout',
        };
        this.geolocationError.set(
          errorKeys[error.code] ?? 'locationPicker.geolocationUnavailable',
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.marker?.off();
    this.map?.off();
    this.map?.remove();
  }

  private selectLocation(latitude: number, longitude: number, recenter = false) {
    this.selectedLatitude.set(latitude);
    this.selectedLongitude.set(longitude);
    this.confirmed.set(false);
    this.renderSelection(recenter);
    this.emitSelection(latitude, longitude, false);
  }

  private emitSelection(
    latitude: number,
    longitude: number,
    isPinConfirmed: boolean,
  ) {
    this.locationChange.emit({ latitude, longitude, isPinConfirmed });
  }

  private renderSelection(recenter: boolean) {
    if (!this.map || !this.leaflet) return;

    const latitude = this.selectedLatitude();
    const longitude = this.selectedLongitude();
    if (latitude === null || longitude === null) {
      this.marker?.remove();
      this.marker = undefined;
      return;
    }

    const position: Leaflet.LatLngTuple = [latitude, longitude];
    if (!this.marker) {
      const icon = this.leaflet.divIcon({
        className: 'location-picker-marker',
        html: '<span aria-hidden="true"></span>',
        iconSize: [32, 40],
        iconAnchor: [16, 40],
      });
      this.marker = this.leaflet
        .marker(position, {
          draggable: true,
          autoPan: true,
          icon,
          alt: this.translate.instant('locationPicker.selectedLocationLabel'),
          title: this.translate.instant('locationPicker.selectedLocationLabel'),
        })
        .addTo(this.map);
      this.marker.on('dragend', () => {
        const point = this.marker?.getLatLng();
        if (point) this.selectLocation(point.lat, point.lng);
      });
    } else {
      this.marker.setLatLng(position);
    }

    if (recenter) this.map.setView(position, Math.max(this.map.getZoom(), 15));
  }

  private renderViewport(viewport: MapViewport) {
    this.map?.setView([viewport.latitude, viewport.longitude], viewport.zoom);
  }
}
